<template>
    <div class="map-container" :style="{width: '100%', height: mapHeight + 'px'}">
        <div id="map" data-nosnippet></div>
        <div class="map-sidebar animate__animated animate__faster animate__fadeInRightBig">
            <InputText
                autocomplete="off"
                id="map-search"
                class="p-mb-3"
                type="text"
                :placeholder="$t('search_pointcash')"
            />
            <Button v-if="navigate" class="navigation-btn p-mb-3" @click="removeNavigation()" v-bind:label="$t('remove_route')" icon="pi pi-trash" iconPos="right"/>
            <div class="map-pos-list" :style="{height: !navigate ? 'calc(100% - 66px)' : 'calc(100% - 132px)'}">
                <p class="p-mt-2 p-mb-2 p-text-justify" v-if="!user_position">{{ $t('no_geoloc') }}</p>
                <p class="p-mt-2 p-mb-2 p-text-justify" v-if="!data.length && user_position">{{ $t('no_results') }}</p>
                <div v-if="!navigate">
                    <div v-for="(pos, index) in data" :key="pos.id" :id="'pos' + index" class="map-pos" :class="{'selected-map-pos': selected_pos === pos.id }" @click="selectPos(index)">
                        <div class="p-grid p-nogutter p-mb-3">
                            <div class="p-col-6">
                                <h5 class="p-mb-0 pos-title p-mt-0"><strong>{{ pos.storename }}</strong></h5>
                            </div>
                            <div class="p-col-6">
                                <span class="pos-dist p-ml-2" v-if="pos.dist">{{ showDistance(pos.dist) }}</span>
                                <span class="pos-map-navi-icon p-text-center" @click="navigateTo(pos, 'BICYCLING', $event)"><i class="fal fa-bicycle"></i></span>
                                <span class="pos-map-navi-icon p-text-center p-mr-1" @click="navigateTo(pos, 'WALKING', $event)"><i class="fal fa-walking"></i></span>
                                <span class="pos-map-navi-icon p-text-center p-mr-1" @click="navigateTo(pos, 'TRANSIT', $event)"><i class="fal fa-subway"></i></span>
                                <span class="pos-map-navi-icon p-text-center p-mr-1" @click="navigateTo(pos, 'DRIVING', $event)"><i class="fal fa-car"></i></span>
                            </div>
                        </div>
                        <p class="p-mb-2 p-mt-0">{{ pos.address }}</p>
                        <p class="p-mb-3 p-mt-0">{{ pos.zipcode }}, {{ pos.city }}</p>
                        <Fieldset legend="Plus de detail" :toggleable="true" :collapsed="true" v-if="Object.keys(pos.openinghours).length">
                            <div v-for="(hours, day) in pos.openinghours" :key="day" class="p-grid p-nogutter">
                                <span class="p-col-3 p-p-2 p-text-right">{{ $t(day) }}: </span>
                                <span class="p-col-9 p-p-2 p-text-left">
                                    <span class="p-p-2" v-for="hour in hours" :key="hour">{{ hour }}</span>
                                </span>
                            </div>
                        </Fieldset>
                    </div>
                </div>
                <div v-else>
                    <div id="navigation-container">
                        <p class="p-mt-0 p-mb-0"></p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import settings from "@/settings"
import {onMounted, onUnmounted, reactive, ref, watch} from "vue";
import MarkerClusterer from "@googlemaps/markerclustererplus";
import {Loader} from "google-maps";
import InputText from "primevue/inputtext";
import Fieldset from "primevue/fieldset";
import Button from "primevue/button";
import axios from "axios";

export default {
    name: 'PublicMapPointCashView',
    components: {
        InputText,
        Fieldset,
        Button
    },
    setup() {
        if (settings.PLATFORM.TRACKING) {
            for (let script of settings.PLATFORM.TRACKING) {
                if (script.local) {
                    import('@/tracking/' + script.url);
                }
                else {
                    let scriptTag = document.createElement('script');
                    scriptTag.src = script.url;
                    scriptTag.async = true;
                    document.head.appendChild(scriptTag);
                }
            }
        }

        let map = null;
        let cluster = null;
        let component = ref(null);
        let initial_position = null;
        let user_position = ref(null);
        let user_position_marker = null;
        const user_position_icon = require('@/assets/theme/images/marker_user_position.png');
        const marker_cluster_icon = 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m';
        const mapHeight = ref(window.innerHeight);

        const api_url = settings.POS_POSITION_PUBLIC_URL;
        const loaded = ref(false);
        const error_loading_data = ref(false);
        const data = reactive([]);
        let markers = [];

        let selected_marker = null;
        const selected_pos = ref(null);
        const navigate = ref(false);
        const marker_icon = require('@/assets/theme/images/marker.png');

        let directionsDisplay = null;
        let directionsService = null;

        function selectPos(index) {
            if (selected_marker) {
                selected_marker.setAnimation(null)
            }
            if (index in data && index in markers) {
                selected_pos.value = data[index].id;
                selected_marker = markers[index];
                selected_marker.setAnimation(window.google.maps.Animation.BOUNCE);
                map.setZoom(14);
                map.setCenter({
                    lat: data[index].latitude,
                    lng: data[index].longitude
                });
            }
        }

        function showDistance(distance) {
            if (distance > 1000) {
                return  (distance / 1000).toFixed(1) + ' km';
            }
            return distance + ' m';
        }

        function removeNavigation() {
            directionsDisplay.setMap(null);
            navigate.value = false;
        }

        function navigateTo(pos, travelMode, event) {
            if (!travelMode) {
                travelMode = window.google.maps.TravelMode.DRIVING;
            }
            let start = user_position.value.lat + ', ' + user_position.value.lng;
            let end = pos.latitude + ', ' + pos.longitude;
            let request = {
                origin: start,
                destination: end,
                travelMode: travelMode
            };
            directionsDisplay.setMap(null);
            directionsService.route(request, function (response, status) {
                if (status === window.google.maps.DirectionsStatus.OK) {
                    directionsDisplay.setDirections(response);
                    directionsDisplay.setMap(map);
                    directionsDisplay.setOptions({suppressMarkers: true});
                    navigate.value = true;
                    setTimeout(function () {
                        let navigation_container = document.getElementById('navigation-container');
                        directionsDisplay.setPanel(navigation_container);
                    }, 100);
                }
                else {
                    console.log(response);
                }
            });
            event.stopPropagation();
        }

        function initMap() {
            const options = {
                center: {lat: 48.856614, lng: 	2.3522219},
                zoomControlOptions: { position: window.google.maps.ControlPosition.BOTTOM_LEFT },
                mapTypeControl: false,
                fullscreenControl: false,
                streetViewControl: false,
                styles: [{"featureType":"all","elementType":"geometry.fill","stylers":[{"weight":"0.50"}]},{"featureType":"all","elementType":"geometry.stroke","stylers":[{"color":"#9c9c9c"}]},{"featureType":"all","elementType":"labels.text","stylers":[{"visibility":"on"}]},{"featureType":"administrative","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"administrative.neighborhood","elementType":"all","stylers":[{"weight":"0.01"},{"invert_lightness":true},{"visibility":"on"}]},{"featureType":"administrative.land_parcel","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"landscape","elementType":"all","stylers":[{"color":"#f2f2f2"},{"visibility":"on"}]},{"featureType":"landscape","elementType":"geometry.fill","stylers":[{"color":"#ffffff"}]},{"featureType":"landscape.man_made","elementType":"geometry.fill","stylers":[{"color":"#ffffff"},{"visibility":"off"}]},{"featureType":"landscape.natural.terrain","elementType":"all","stylers":[{"weight":"0.01"},{"visibility":"off"}]},{"featureType":"poi","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"poi.attraction","elementType":"all","stylers":[{"visibility":"off"},{"lightness":"-27"}]},{"featureType":"poi.business","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"poi.government","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"poi.park","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"poi.place_of_worship","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"poi.school","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"poi.sports_complex","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"road","elementType":"all","stylers":[{"saturation":-100},{"lightness":45}]},{"featureType":"road","elementType":"geometry.fill","stylers":[{"color":"#eeeeee"}]},{"featureType":"road","elementType":"labels.text.fill","stylers":[{"color":"#7b7b7b"}]},{"featureType":"road","elementType":"labels.text.stroke","stylers":[{"color":"#ffffff"}]},{"featureType":"road.highway","elementType":"all","stylers":[{"visibility":"simplified"}]},{"featureType":"road.arterial","elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"transit","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"transit.station","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"water","elementType":"labels.text.fill","stylers":[{"color":"#070707"}]},{"featureType":"water","elementType":"labels.text.stroke","stylers":[{"color":"#ffffff"}]}],
                zoom: 8
            }
            map = new window.google.maps.Map(document.getElementById('map'), options);
            cluster = new MarkerClusterer(map, [], {imagePath: marker_cluster_icon});
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(function (position) {
                    user_position.value = {lat:position.coords.latitude, lng:position.coords.longitude};
                    initial_position = user_position.value;
                    map.setCenter(user_position.value);
                    map.setZoom(12);
                    user_position_marker = new window.google.maps.Marker({
                        position: user_position.value,
                        icon: user_position_icon,
                        map: map,
                    });
                }, function(error) {
                    console.log(error);
                });
            }
            else {
                console.log('geolocation not available');
            }
            getData(api_url);
        }

        function initSearchBox() {
            const input = document.getElementById("map-search");
            if (input) {
                const searchBox = new window.google.maps.places.SearchBox(input);
                searchBox.addListener("places_changed", () => {
                    const places = searchBox.getPlaces();
                    if (!places.length) {
                        return;
                    }
                    user_position.value = {
                        lat:places[0].geometry.location.lat(),
                        lng:places[0].geometry.location.lng()
                    };
                    map.setCenter(user_position.value);
                    map.setZoom(15);
                    if (user_position_marker) {
                        user_position_marker.setPosition(user_position.value);
                    }
                    else {
                        user_position_marker = new window.google.maps.Marker({
                            position: user_position.value,
                            icon: user_position_icon,
                            map: map,
                        });
                    }
                });
                input.addEventListener("keyup", event => {
                    if (!event.target.value) {
                        user_position.value = initial_position ? initial_position : null;
                        if (user_position_marker && user_position.value) {
                            user_position_marker.setPosition(user_position.value);
                        }
                        if (user_position.value) {
                            map.setCenter(user_position.value);
                        }
                    }
                });
            }
        }

        function getData(url) {
            if (!user_position.value) {
                data.push(...[]);
                loadMarkers(data);
                return;
            }
            let params = {
                latitude: user_position.value.lat,
                longitude: user_position.value.lng,
            };
            axios({method: 'get', url: url, params: params}).then(
                (response) => {
                    data.push(...response.data);
                    loadMarkers(data);
                },
                () => {
                    error_loading_data.value = true;
                }
            );
        }

        function loadMarkers(new_markers) {
            for (let i = 0; i < new_markers.length; i++) {
                let marker = new window.google.maps.Marker({
                    position: {lat:  new_markers[i].latitude, lng:  new_markers[i].longitude},
                    icon: marker_icon,
                    map: map
                });
                marker.addListener('click', function () {
                    if (selected_marker) {
                        selected_marker.setAnimation(null)
                    }
                    selected_pos.value = data[i].id;
                    selected_marker = markers[i];
                    selected_marker.setAnimation(window.google.maps.Animation.BOUNCE);
                    let elem = document.getElementById("pos" + i);
                    if (elem) {
                        elem.scrollIntoView();
                    }
                });
                markers.push(marker);
            }
            cluster.addMarkers(markers);
            loaded.value = true;
            setTimeout(function () {
                initSearchBox();
            }, 1000);
        }

        function clearMarkers() {
            loaded.value = false;
            for (let i = 0; i < markers.length; i++) {
                markers[i].setMap(null);
            }
            markers.clear();
            cluster.clearMarkers();
        }

        function resizeMap() {
            mapHeight.value = window.innerHeight;
        }

        onMounted(function() {
            if (!window.google) {
                let api_key = settings.PLATFORM.GG_API_KEY ? settings.PLATFORM.GG_API_KEY : settings.GG_API_KEY;
                const loader = new Loader(api_key, {
                    libraries: ['places'],
                });
                loader.load().then(function (google) {
                    window.google = google;
                    directionsDisplay = new window.google.maps.DirectionsRenderer();
                    directionsService = new window.google.maps.DirectionsService();
                    initMap();
                });
            }
            else {
                initMap();
            }
            window.addEventListener("resize", resizeMap);
        });

        onUnmounted(function () {
            window.removeEventListener("resize", resizeMap);
        });

        watch(() => user_position.value, function() {
            data.clear();
            clearMarkers();
            getData(api_url);
        });

        return {
            data,
            navigate,
            user_position,
            selected_pos,
            mapHeight,
            component,
            loaded,
            error_loading_data,
            selectPos,
            removeNavigation,
            showDistance,
            navigateTo
        }
    }
}
</script>

<style lang="scss">
@import "src/assets/theme/scss/variable";
#map {
    width: 100%;
    height: 100%;
}
.map-container {
    position: relative;
}
.map-progress-bar {
    position: absolute;
    z-index: 1;
    width: 350px;
    background-color: white;
    overflow-y: auto;
    padding: 15px;
    background-clip: border-box;
    border: 0 solid #f7f7f7;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 20%);
    color: var(--text-color);
    right: 10px;
    top: 10px;
}
.map-sidebar {
    width: 500px;
    height: 100%;
    position: absolute;
    z-index: 1;
    background-color: white;
    overflow-y: auto;
    padding: 15px;
    background-clip: border-box;
    border: 0 solid #f7f7f7;
    border-radius: .25rem;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 20%);
    color: var(--text-color);
    right: 0;
    top: 0;
}
#map-search {
    width: 100%;
    transition: width calc(var(--animate-duration) / 2) ease;
    top: 10px;
    left: 10px;
    z-index: 1;
}
.map-pos {
    padding: 20px 10px 20px 10px;
    border-bottom: 1px solid #DCDDDF;
    cursor: pointer;
}
.map-pos:hover, .map-pos:hover .pos-title, .map-pos:hover .pos-map-navi-icon, .map-pos:hover .pos-dist,
.selected-map-pos, .selected-map-pos .pos-title, .selected-map-pos .pos-map-navi-icon, .selected-map-pos .pos-dist {
    background-color: $primary-color !important;
    color: $button-text-color;
}
.map-pos:hover .p-fieldset, .selected-map-pos .p-fieldset {
    background-color: $primary-color !important;
    color: $button-text-color;
}
.p-fieldset {
    border: none !important;
}
.map-pos:hover .p-fieldset a, .selected-map-pos .p-fieldset a {
    background-color: $primary-color !important;
    color: $map-hover-color !important;
}
.p-fieldset a {
    color: $secondary-color !important;
    border-radius: 0 !important;
}
.p-fieldset .p-fieldset-legend {
    border: none !important;
}
.map-pos h5 {
    line-height: 30px;
}
.pos-map-navi-icon {
    color: $primary-color;
    font-size: .875rem;
    display: inline-block;
    background-color: rgba(0, 51, 161, .1);
    height: 30px;
    width: 30px;
    text-align: center;
    line-height: 30px;
    float: right;
    padding: 0 6px 0 6px;
    cursor: pointer;
}
.map-pos-list {
    overflow-y: auto;
}
.navigation-btn {
    width: 100%;
    padding: 16px 24px 16px 24px !important;
}
.pos-title {
    line-height: 30px;
    font-weight: 500;
    font-size: 0.95rem;
    color: var(--text-color);
}
.pos-map p {
    font-size: .875rem;
    color: var(--text-color);
}
.pos-dist {
    float: right;
    display: inline-block;
    line-height: 30px;
    color: #6c757d;
}
#navigation-container {
    width: 100%;
}
</style>
